import React, { useEffect, useMemo, useState } from 'react';
import { BUSINESS_RULES_AUDIT_FINDINGS_COLUMNS } from 'constants/reports';
import { BusinessRuleData } from 'store/ducks/Reporting/types';
import { useFilterOptions } from 'utils/hooks/useFilterOptions';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import * as Styled from './BusinessRulesAuditFindings.styled';
import { useModuleOptions } from 'utils/hooks/useModule';
import { useReportExport } from 'utils/hooks/useReportExport';
import { stripHtmlTags } from 'utils/stripHtmlTags';

const BusinessRulesAuditFindings: React.FC = () => {
  const { loading } = useFilterOptions();
  const [data, setData] = useState<BusinessRuleData[]>([]);
  const { registerReport, clearReport } = useReportExport();

  const columns = useMemo(() => BUSINESS_RULES_AUDIT_FINDINGS_COLUMNS, []);

  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const normalizedFilters = Helpers.normalizeFilters(
    Helpers.toApiFilters(orgContext)
  );

  const industry = orgContext?.industryType || [];

  const { moduleOptionsState, fetchModules } = useModuleOptions(industry);

  const handleDisplay = async () => {
    try {
      if (!orgContext || Object.keys(orgContext).length === 0) return;

      const hasFilters = Object.keys(filterParams).length > 0;
      if (!hasFilters) return;

      const modules =
        moduleOptionsState.length > 0
          ? moduleOptionsState
          : await fetchModules();

      const selectedModule = modules.find(
        (module) => Number(module.value) === Number(normalizedFilters.module)
      );

      const apiFilters = {
        ...normalizedFilters,
        module: selectedModule?.label ?? normalizedFilters.module
      };

      const response = await ApiService.getBruAuditGaps(lang, apiFilters);
      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      const formattedData = responseData.map((item: any, index: number) => {
        const gaps = Object.values(item.GAPS || {});

        return {
          ...item,
          parent_key: item.parent_child,
          child_key: item.child_child,
          key: item.id || index,
          value: stripHtmlTags(item.value),
          gap_desc: gaps.map((g: any) => stripHtmlTags(g.gap_desc)),
          gap_due_date: gaps.map((g: any) => g.gap_due_date),
          gap_closed: gaps.map((g: any) => g.gap_closed),
          gap_priority: gaps.map((g: any) => g.gap_priority),
          gap_source: gaps.map((g: any) => g.gap_source),
          gap_owner_mail: gaps.map((g: any) => g.gap_owner_mail),
          gap_created_on: gaps.map((g: any) => g.gap_created_on),
          gap_noticed: gaps.map((g: any) => g.gap_noticed),
          gap_owner_notified: gaps.map((g: any) => g.gap_owner_notified)
        };
      });

      setData(formattedData);
    } catch (error) {
      console.error('Failed to fetch BRU Audit Gaps data:', error);
    }
  };

  useEffect(() => {
    registerReport(
      data,
      columns,
      'Business_Rules_Audit_Findings',
      'Business Rules & Audit Findings'
    );
  }, [data, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  useEffect(() => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    handleDisplay();
  }, [lang, orgContext]);

  return (
    <Styled.VerticalSpace direction='vertical' size='large'>
      <CustomTable
        drawTable={true}
        itemsCount={data.length}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1600, y: 400 }}
        loading={loading}
        rowKey={(record: BusinessRuleData) => record.key}
        sticky
      />
    </Styled.VerticalSpace>
  );
};

export default BusinessRulesAuditFindings;
